import React, { useState, useEffect, createContext, useContext } from 'react';
import api from '../utils/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  const token = localStorage.getItem('token');
  if (token) {
    api.setToken(token);
    api.get('/api/auth/me')
      .then(data => setUser(data))
      .catch(() => {
        api.setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  } else {
    setLoading(false);
  }
}, []);

const refreshUser = async () => {
  const currentUser = await api.get('/api/auth/me');
  setUser(currentUser);
  return currentUser;
};

const login = async (email, password) => {
  const data = await api.post('/api/auth/login', { email, password });
  
  // Handle OTP required response
  if (data.status === 'otp_required') {
    return data; // Return the OTP response to let LoginPage handle it
  }
  
  // Handle direct authentication (no OTP needed)
  api.setToken(data.access_token);
  setUser(data.user);
  await refreshUser();
  return data;
};

const completeOTPVerification = (userData, accessToken) => {
  // Called after OTP verification is successful
  api.setToken(accessToken);
  setUser(userData);
  refreshUser().catch(() => {
    // Keep the authenticated user if the entitlement refresh is temporarily unavailable.
  });
};

  const logout = () => {
    api.setToken(null);
    setUser(null);
  };

  // Fine-grained RBAC (backend models/rbac.py) — additive to `user.role`.
  // `user.permissions` is a flat string[] resolved server-side by
  // services/permission_service.py and returned from /api/auth/me. Empty
  // for a school that hasn't run scripts/seed_permissions.py yet, so
  // `can()` is only meaningful for endpoints actually migrated to
  // require_permission() on the backend — existing `user.role === '...'`
  // checks throughout the app are untouched and keep working regardless.
  const can = (code) => Boolean(user?.permissions?.includes(code));

  // Plan-tier feature entitlements (backend services/plan_gating.py),
  // returned alongside `permissions` from /api/auth/me. Login and OTP
  // verification refresh /me immediately so this reflects the current
  // BillingConfiguration.plan_tier instead of a stale or incomplete session.
  // UI-only — the real enforcement is the 403 each gated backend router
  // already returns.
  const hasModule = (moduleKey) => Boolean(user?.plan_modules?.includes(moduleKey));

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, completeOTPVerification, can, hasModule }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
